import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * BottomSheet — iOS-style modal that slides up from the bottom on mobile.
 * Falls back to a centered card on desktop.
 *
 * Rendered through a React portal to document.body so it escapes any ancestor
 * with a `transform` (which would otherwise turn `position: fixed` into
 * `position: absolute` relative to that ancestor — breaks centering).
 */
export default function BottomSheet({ open, onClose, title, children, footer }) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    if (panelRef.current) panelRef.current.scrollTop = 0;
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const sheet = (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      data-testid="bottom-sheet"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="w-full md:max-w-lg md:rounded-2xl rounded-t-2xl bottom-sheet-panel"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          maxHeight: "92vh",
          overflowY: "auto",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile only) */}
        <div className="md:hidden flex justify-center pt-3 pb-1">
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 999,
              background: "var(--border)",
            }}
          />
        </div>

        {/* Title bar — sticky so it always stays visible */}
        {title && (
          <div
            className="flex items-center justify-between px-5 pt-4 pb-3 sticky top-0 z-10"
            style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}
          >
            <h3 className="text-lg font-semibold" data-testid="bottom-sheet-title">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition hover:bg-white/5"
              style={{ color: "var(--text-muted)" }}
              data-testid="bottom-sheet-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className={`px-5 pt-2 ${footer ? "pb-24" : "pb-4"}`}>{children}</div>

        {/* Footer */}
        {footer && (
          <div
            className="px-5 py-3 sticky bottom-0"
            style={{ background: "var(--surface)", borderTop: "1px solid var(--border)" }}
          >
            {footer}
          </div>
        )}
      </div>

      <style>{`
        .bottom-sheet-panel {
          animation: bs-slide-up 0.25s ease-out;
        }
        @keyframes bs-slide-up {
          from { transform: translateY(20px); opacity: 0.85; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (min-width: 768px) {
          .bottom-sheet-panel { animation: bs-fade-in 0.18s ease-out; }
          @keyframes bs-fade-in {
            from { transform: scale(0.96); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        }
      `}</style>
    </div>
  );

  return createPortal(sheet, document.body);
}
